<template>
  <v-dialog v-model="visible" persistent max-width="638">
    <v-card class="v-dialog--custom" v-if="visible">
      <v-card-title class="headline pa-4">
        <span class="v-dialog--custom__title">{{ $t(`${namespace}.exceptionList`) }}</span>
        <v-btn
          class="v-dialog--custom__close mr-2"
          fab
          flat
          color="gray-6"
          :disabled="isFetching"
          @click.native="close"
        >
          <v-icon dark>close</v-icon>
        </v-btn>
      </v-card-title>

      <form
        class="form--custom"
        novalidate
        @submit.prevent="handleSubmit"
      >
        <v-card-text class="px-4 pt-0 pb-4">
          <div
            v-if="claims.timePolicy['ROLE_POST_TIMEPOLICY_BYPASS']"
            class="autocomplete__container"
            style="height: 95px;"
          >
            <label class="gray-6--text">{{ $t(`${namespace}.autocompleteLabel`) }}</label>
            <div v-if="isSuccess">
              <AutoComplete
                kind="employee"
                :placeholder="$t(`${namespace}.autocompletePlaceholder`)"
                :isValid="isEmployeeValid"
                required
                @input="updateEmployee"
              />

              <v-btn
                color="accent"
                style="font-size: 12px;"
                type="submit"
                depressed
                round
              >
                {{ $t(`${namespace}.addToList`) }}
              </v-btn>
            </div>
          </div>

          <div class="text-xs-center">
            <span>{{ $t(`${namespace}.autocompleteMessage`) }}</span>
          </div>
        </v-card-text>

        <!-- table -->
        <div class="table table-hover">
          <!-- table header -->
          <v-layout
            align-center
            class="grey--text text--darken-4 px-3 caption grey lighten-4 has-border-b table-header"
          >
            <v-flex
              class="checkbox--reset"
              style="max-width: 38px;"
              xs1
            >
              &nbsp;
            </v-flex>
            <v-flex xs11>
              {{ $t('Common.employeeName') }}
            </v-flex>
          </v-layout>

          <!-- success -->
          <div v-if="isSuccess || employeeList.length">

            <!-- with results -->
            <v-list
              v-if="employeeList.length"
              class="v-list--reset table-body"
              subheader
              two-line
            >
              <div v-for="(item, i) in employeeList" :key="i">
                <v-list-tile class="list__item">
                  <v-list-tile-content>
                    <v-list-tile-sub-title>
                      <v-layout align-center class="grey--text text--darken-4 px-3">
                        <v-flex
                          v-if="claims.timePolicy['ROLE_DELETE_TIMEPOLICY_BYPASS_EMPLOYEEID']"
                          class="checkbox--reset"
                          style="max-width: 38px;"
                          xs1
                        >
                          <button
                            type="button"
                            :title="$t('Common.delete')"
                            class="table-body__actions"
                            slot="activator"
                            @click="removeFromExceptionList(item.employeeId)"
                          >
                            <v-icon>delete</v-icon>
                          </button>
                        </v-flex>
                        <v-flex class="text-capitalize" xs11>
                          <span :title="item.employeeName">{{ item.employeeName }}</span>
                        </v-flex>
                      </v-layout>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-divider v-if="i < employeeList.length" />
              </div>
            </v-list>

            <!-- without results -->
            <div class="pt-5 pb-4" v-else>
              <EmptyState
                image="usage-policy"
                :text="$t(`${namespace}.emptyStateMessage`)"
              />
            </div>
          </div>
        </div>

        <!-- error -->
        <div v-if="isFailed" class="text-xs-center">
          <div class="py-5">
            {{ $t('Common.genericErrorMsg') }}<br>
            <a
              href="#"
              :title="$t('Common.tryAgain')"
              class="text--link accent--text"
              @click.prevent="getTimePolicy"
            >
              {{ $t('Common.tryAgain') }}.
            </a>
          </div>
          <v-divider />
        </div>

        <!-- loading -->
        <div v-if="isLoading" class="text-xs-center">
          <v-progress-circular class="my-3" indeterminate color="accent" :size="40" />
          <v-divider />
        </div>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { hasRole } from 'utils/functions'
import AutoComplete from 'components/AutoComplete'
import EmptyState from 'components/EmptyState'

export default {
  name: 'ExceptionListDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    timePolicy: {
      type: Object
    }
  },
  components: {
    AutoComplete,
    EmptyState
  },
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.UsagePoliciesPage',
      currentStatus: null,
      isFetching: false,
      isEmployeeValid: true,
      employeeId: null,
      employeeList: []
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error',
      infoAlert: 'snackbarAlert/info'
    }),

    hasRole (group, role) {
      return hasRole(group, role)
    },

    close () {
      this.$emit('close')
    },

    resetFields () {
      this.currentStatus = null
      this.isFetching = false
      this.isEmployeeValid = true
      this.employeeId = null
      this.employeeList = []
    },

    updateEmployee (val) {
      if (!val || !Object.keys(val).length) {
        this.employeeId = null
        return
      }
      this.employeeId = val.id
    },

    async getExceptionList () {
      if (!this.hasRole('timePolicy', 'ROLE_GET_TIMEPOLICY_BYPASS')) return

      this.resetFields()
      this.currentStatus = STATUS_IS_LOADING

      try {
        const company = this.company ? this.company.id : null
        const { data: res } = await this.$apiService.getExceptionList({ company })
        this.employeeList = res.data
        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('Error', err)
        this.currentStatus = STATUS_FAILED
      }
    },

    async removeFromExceptionList (employeeId) {
      try {
        await this.$apiService.removeFromExceptionList(employeeId)
        this.successAlert(this.$t(`${this.namespace}.deleteEmployeeExceptionListSuccess`))
        this.getExceptionList()
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.deleteEmployeeExceptionListError`))
      }
    },

    async handleSubmit () {
      if (!this.employeeId) {
        this.isEmployeeValid = false
      }

      try {
        const isValid = await this.$validator.validateAll()
        if (!isValid || !this.employeeId) return

        const duplicateEmployee = this.employeeList.find(item => item.employeeId === this.employeeId)
        if (duplicateEmployee) {
          this.infoAlert(this.$t(`${this.namespace}.duplicateEmployeeMessage`))
          return
        }

        await this.$apiService.addToExceptionList({ employeeId: this.employeeId })
        this.successAlert(this.$t(`${this.namespace}.updateExceptionListSuccess`))
        this.getExceptionList()
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.updateExceptionListError`))
      }
    }
  },
  mounted () {
    this.getExceptionList()
  },
  watch: {
    visible (val) {
      this.getExceptionList()
      val && this.$ga.page('/dialog/politicas-de-uso/lista-de-excecao')
    }
  }
}
</script>

<style lang="scss">
.autocomplete__container {
  position: relative;

  .v-btn {
    position: absolute;
    right: 0;
    top: 25px;
  }
}

.v-dialog--custom {

  .table .table-header,
  .table .table-body {
    min-width: 100%;
  }
}
</style>
